import { useEffect, useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import { useLookupSearch } from '../components/Lookup/hooks/useLookupSearch';
import { useLookupState } from '../components/Lookup/hooks/useLookupState';
import { useAbortController } from '../hooks/useAbortController';
import LookupSearchBar from '../components/Lookup/LookupSearchBar';
import LookupResultsTable from '../components/Lookup/LookupResultsTable';
import LookupFiltersPanel from '../components/Lookup/LookupFiltersPanel';
import LookupModals from '../components/Lookup/LookupModals';
import { getMetricValue, getCountryCodes } from '../components/Lookup/helpers';
import { PagesHelper } from '../common/PagesHelper';
import { TrafficSources } from '../services/TrafficSources';

interface LookupPageProps {
  currentUser?: any;
}

const LookupPage = ({ currentUser }: LookupPageProps) => {
  // Custom hooks
  const {
    searchTerm,
    loading,
    apps,
    error,
    filters,
    displayLimit,
    hasMoreResults,
    isFetching,
    observerRef,
    setSearchTerm,
    setApps,
    setError,
    setFilters,
    performSearch,
    fetchDetailedAppData,
    clearFilters
  } = useLookupSearch();

  const {
    sortColumn,
    sortDirection,
    sortTimeframe,
    showStats,
    setShowStats,
    handleSort,
    showStatsModal,
    setShowStatsModal,
    showEditClinkModal,
    setShowEditClinkModal,
    showTrackingLinksModal,
    setShowTrackingLinksModal,
    showHistoryModal,
    setShowHistoryModal,
    showCreateClinkModal,
    selectedApp,
    selectedClink,
    selectedAppForClink,
    selectedCampaignId,
    selectedDemandSourceId,
    hasInitialLoad,
    setHasInitialLoad,
    handleShowStats,
    handleCreateClink,
    handleCloseCreateClink,
    handleEditClink,
    handleShowTrackingLinks,
    handleShowHistory
  } = useLookupState();
  const { createController } = useAbortController();

  // Load demand sources and traffic sources data
  useEffect(() => {
    if (currentUser) {
      const controller = createController();
      PagesHelper.loadDemandSourcesHelper(controller);
      TrafficSources.getTrafficSources(controller);
    }
  }, [currentUser, createController]);

  // Auto-load results on page entry with saved search term
  useEffect(() => {
    if (!hasInitialLoad) {
      performSearch(searchTerm);
      setHasInitialLoad(true);
    }
  }, [performSearch, searchTerm, hasInitialLoad, setHasInitialLoad]);

  // Auto-search when filters change (but not on initial load)
  useEffect(() => {
    if (hasInitialLoad) {
      performSearch(searchTerm);
    }
  }, [filters, hasInitialLoad, searchTerm, performSearch]);

  // Re-sort when showStats changes (since metric values will be different)
  useEffect(() => {
    if (apps.length > 0) {
      setApps(prevApps => {
        const sortedApps = [...prevApps].sort((a, b) => {
          let aValue: any, bValue: any;

          switch (sortColumn) {
            case 'title':
              aValue = a.title || '';
              bValue = b.title || '';
              break;
            case 'countries':
              aValue = getCountryCodes(a);
              bValue = getCountryCodes(b);
              break;
            case 'clicks':
            case 'fillRate':
            case 'conversions':
            case 'conversionRate':
            case 'revenue':
              aValue = getMetricValue(a, sortColumn, sortTimeframe, showStats);
              bValue = getMetricValue(b, sortColumn, sortTimeframe, showStats);
              break;
            default:
              return 0;
          }

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            const comparison = aValue.localeCompare(bValue);
            return sortDirection === 'asc' ? comparison : -comparison;
          }

          const comparison = aValue - bValue;
          if (comparison === 0) {
            // Secondary sort by title for consistent ordering when values are equal
            const titleA = a.title || '';
            const titleB = b.title || '';
            return titleA.localeCompare(titleB);
          }
          return sortDirection === 'asc' ? comparison : -comparison;
        });

        return sortedApps;
      });
    }
  }, [showStats, sortColumn, sortDirection, sortTimeframe, apps.length, setApps]);

  const handleSearch = () => {
    performSearch(searchTerm);
  };

  // Sort the apps
  const sortedApps = apps.length === 0 ? [] : [...apps].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortColumn) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'countries':
        aValue = getCountryCodes(a);
        bValue = getCountryCodes(b);
        break;
      case 'clicks':
      case 'fillRate':
      case 'conversions':
      case 'conversionRate':
      case 'revenue':
        aValue = getMetricValue(a, sortColumn, sortTimeframe, showStats);
        bValue = getMetricValue(b, sortColumn, sortTimeframe, showStats);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    
    // When values are equal, use alphabetical order as secondary sort
    if (sortColumn !== 'title') {
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      if (aTitle < bTitle) return -1;
      if (aTitle > bTitle) return 1;
    }
    
    return 0;
  });
  
  // Apply display limit for pagination
  const displayedApps = sortedApps.slice(0, displayLimit);

  const handleExpandApp = useCallback(async (app: any) => {
    try {
      // If collapsing (app is currently expanded), just toggle
      if (app.expanded) {
        const updatedApps = apps.map(a => {
          if (a.appID === app.appID) {
            return { ...a, expanded: false };
          }
          return a;
        });
        setApps(updatedApps);
        return;
      }

      // If expanding, first toggle the state to show loading, then fetch detailed data
      const updatedApps = apps.map(a => {
        if (a.appID === app.appID) {
          return {
            ...a,
            expanded: true,
            selectedTab: a.selectedTab || 'matchingCampaigns',
            loadingDetails: true
          };
        }
        return a;
      });
      setApps(updatedApps);

      // Fetch detailed campaign and clink data for this specific app
      const detailedApp = await fetchDetailedAppData(app);
      
      if (detailedApp) {
        // Update the app with detailed data
        setApps(currentApps => currentApps.map(a => {
          if (a.appID === app.appID) {
            return {
              ...a,
              ...detailedApp,
              expanded: true,
              selectedTab: a.selectedTab || 'matchingCampaigns',
              loadingDetails: false
            };
          }
          return a;
        }));
      } else {
        // If failed to fetch, remove loading state
        setApps(currentApps => currentApps.map(a => {
          if (a.appID === app.appID) {
            return {
              ...a,
              expanded: true,
              selectedTab: a.selectedTab || 'matchingCampaigns',
              loadingDetails: false
            };
          }
          return a;
        }));
      }
    } catch (error) {
      console.error('Error in handleExpandApp:', error);
      // Remove loading state on error
      setApps(currentApps => currentApps.map(a => {
        if (a.appID === app.appID) {
          return {
            ...a,
            expanded: true,
            selectedTab: a.selectedTab || 'matchingCampaigns',
            loadingDetails: false
          };
        }
        return a;
      }));
    }
  }, [apps, setApps, fetchDetailedAppData]);

  const handleSelectTab = (app: any, tabName: string) => {
    const updatedApps = apps.map(a => {
      if (a.appID === app.appID) {
        return { ...a, selectedTab: tabName };
      }
      return a;
    });
    setApps(updatedApps);
  };

  const handleReloadClink = () => {
    setShowEditClinkModal(false);
    performSearch(searchTerm);
  };

  const exportCsv = () => {
    // Build the same parameters as the search
    const params: any = {
      search: searchTerm,
      returnStats: true,
      returnCampaigns: true,
      returnClinks: true,
      includeNonMatchingCampaigns: true,
      useExpandedSearchQuery: true,
      formattedOptimizations: true,
      export: 'csv'
    };

    // Add filter parameters (simplified version of the filter logic)
    if (filters.selectedCampaignStats && filters.selectedCampaignStats.length > 0) {
      const selectedIds = filters.selectedCampaignStats.map(stat => stat.id);
      params.includeActive = selectedIds.includes('active');
      params.includeInactive = selectedIds.includes('inactive');
      params.includeNeedsApproval = selectedIds.includes('needs-approval');
      params.includeNoWhitelist = selectedIds.includes('no-whitelist');
      params.includePayoutIssues = selectedIds.includes('payout-issues');
      params.includeNotInFeed = selectedIds.includes('removed-from-feed');
      params.includeRegionTargeted = selectedIds.includes('region-targeted');
      params.includeNonRegionTargeted = selectedIds.includes('non-region-targeted');
    }

    if (filters.selectedCampaignHealth && filters.selectedCampaignHealth.length > 0) {
      const selectedIds = filters.selectedCampaignHealth.map(health => health.id);
      params.includeHealthyQC = selectedIds.includes('healthy-qc');
      params.includeUnhealthyQC = selectedIds.includes('unhealthy-qc');
      params.includeBrokenStats = selectedIds.includes('broken-stats');
      params.includeWorkingStats = selectedIds.includes('healthy-stats');
    }

    if (filters.selectedActivityStatus.length > 0) {
      const selectedIds = filters.selectedActivityStatus.map(activity => activity.id);
      params.reqActivityClicks24h = selectedIds.includes('clicks-24h');
      params.reqActivityClicks7d = selectedIds.includes('clicks-7d');
      params.reqActivityInstalls24h = selectedIds.includes('conversions-24h');
      params.reqActivityInstalls7d = selectedIds.includes('conversions-7d');
      params.activityNearDailyCap = selectedIds.includes('near-cap');
    }

    if (filters.selectedClinksStatus) {
      params.clinkFilter = filters.selectedClinksStatus.id;
    }
    
    if (filters.minDemandSources !== null && filters.minDemandSources > 0) {
      params.minDemandSources = filters.minDemandSources;
    }
    if (filters.maxDemandSources !== null && filters.maxDemandSources > 0) {
      params.maxDemandSources = filters.maxDemandSources;
    }

    if (filters.selectedDemandSources.length > 0) {
      params.allowDemandSources = filters.selectedDemandSources.map(s => s.demandSourceId).join(',');
    }

    if (filters.selectedCountries && filters.selectedCountries.length > 0) {
      params.countries = filters.selectedCountries.map(c => c.id).join(',');
    }

    if (filters.selectedPlacements && filters.selectedPlacements.length > 0) {
      params.placements = filters.selectedPlacements.map(p => p.publisherPlacementId).join(',');
    }

    // Create the query string
    const queryString = new URLSearchParams(params as any).toString();

    // Open the CSV export URL in a new window
    const url = `${process.env.REACT_APP_JUPITER_API_BASE_URL}/admin/campaigns/query?${queryString}`;
    window.open(url, '_blank');
  };

  return (
    <div id="lookup-container" style={{ paddingTop: "15px" }}>
      <div className="products-lookup-react p-3" style={{width: '100%'}}>
        {/* Search Bar */}
        <LookupSearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          loading={loading}
          onSearch={handleSearch}
          onClearFilters={clearFilters}
          onExportCsv={exportCsv}
        />

        {/* Filters Panel */}
        <div className="filters-panel mb-4">
          <LookupFiltersPanel
            filters={filters}
            setFilters={setFilters}
          />
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}


        {/* Results Table */}
        <LookupResultsTable
          loading={loading}
          sortedApps={sortedApps}
          displayedApps={displayedApps}
          displayLimit={displayLimit}
          hasMoreResults={hasMoreResults}
          isFetching={isFetching}
          observerRef={observerRef}
          showStats={showStats}
          setShowStats={setShowStats}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortTimeframe={sortTimeframe}
          onSort={handleSort}
          onShowStats={handleShowStats}
          onCreateClink={handleCreateClink}
          onExpandApp={handleExpandApp}
          onSelectTab={handleSelectTab}
          onEditClink={handleEditClink}
          onShowTrackingLinks={handleShowTrackingLinks}
          onShowHistory={handleShowHistory}
        />

        {/* All Modals */}
        <LookupModals
          showStatsModal={showStatsModal}
          setShowStatsModal={setShowStatsModal}
          selectedApp={selectedApp}
          currentUser={currentUser}
          showCreateClinkModal={showCreateClinkModal}
          selectedAppForClink={selectedAppForClink}
          selectedCampaignId={selectedCampaignId}
          selectedDemandSourceId={selectedDemandSourceId}
          onCloseCreateClink={handleCloseCreateClink}
          showEditClinkModal={showEditClinkModal}
          setShowEditClinkModal={setShowEditClinkModal}
          selectedClink={selectedClink}
          onReloadClink={handleReloadClink}
          showTrackingLinksModal={showTrackingLinksModal}
          setShowTrackingLinksModal={setShowTrackingLinksModal}
          showHistoryModal={showHistoryModal}
          setShowHistoryModal={setShowHistoryModal}
        />
      </div>
    </div>
  );
};

export default LookupPage;